import { useEffect, useState } from 'react';
import { IBooking } from '@whitelabel/helpers/types';
import { deleteFalsyParams } from '@whitelabel/helpers/url';
import { RC_BANNER_FEATURE_FLAG_KEY } from '@whitelabel/xcover-shared/helpers/constants';
import { useGetFeatureFlagsQuery } from '../store/services/xcover/endpoints/features';
import { isFeatureEnabledForPartner } from './featureFlags';

const useShowRCUpsellBanner = (bookings: IBooking[], customerId: string) => {
  const [showRCupsellBanner, setShowRCupsellBanner] = useState<boolean>(false);
  const [urlQueryObj, setUrlQueryObj] = useState<Record<string, unknown>>({});

  const latestTravelBooking = bookings?.find(
    (item) => Object.values(item.policyTypes)[0].quotes[0].policy.policyTypeGroupName === 'travel',
  );

  const { data: featureFlagsData, isFetching } = useGetFeatureFlagsQuery(undefined, { skip: !latestTravelBooking });

  const getPolicyTypeCode = (departure: string, destination: string) => {
    if (departure === 'US' || departure === 'CA' || destination === 'US') return 'CDW';
    return 'FullProtection';
  };

  const getTravelBookingParam = (booking?: IBooking) => {
    if (!booking) {
      return {
        CountryOfTravelCode: '',
        CustomerCountryCode: '',
        PolicyType: '',
        FromDate: '',
        ToDate: '',
        partnerId: '',
      };
    }
    const { attributes } = Object.values(booking.policyTypes)[0].quotes[0];
    const { tripStartDate, tripEndDate, destinationCountry } = attributes;
    const { id: partnerId } = booking.partner;

    const policyTypeCode = getPolicyTypeCode(booking.policyholder.country, destinationCountry);
    return {
      CountryOfTravelCode: destinationCountry || '',
      CustomerCountryCode: booking.policyholder.country,
      PolicyType: policyTypeCode,
      FromDate: tripStartDate?.slice(0, 10) || '',
      ToDate: tripEndDate?.slice(0, 10) || '',
      partnerId,
    };
  };

  const isFuturePolicy = (booking: IBooking) => {
    const today = new Date();
    const tripStartDate = new Date(Object.values(booking.policyTypes)[0].quotes[0].attributes.tripStartDate);
    if (!tripStartDate) return false;
    return today < tripStartDate;
  };

  const { CountryOfTravelCode, CustomerCountryCode, PolicyType, FromDate, ToDate, partnerId } =
    getTravelBookingParam(latestTravelBooking);

  const constructUrlQueryObj = (booking?: IBooking) => {
    if (!booking) return {};

    return deleteFalsyParams({
      CountryOfTravelCode,
      CustomerCountryCode,
      PolicyType,
      ...(FromDate &&
        ToDate &&
        isFuturePolicy(booking) && {
          FromDate,
          ToDate,
        }),
      VehicleType: 'car',
      utm_source: 'xcover',
      utm_campaign: 'account-banner-v1', // This string is for utm purpose, only used for RC banner on /account page.
      utm_customer: customerId,
    });
  };

  useEffect(() => {
    if (!featureFlagsData) {
      return;
    }

    const isNotBlacklistedPartner = isFeatureEnabledForPartner({
      featureFlags: featureFlagsData,
      featureKey: RC_BANNER_FEATURE_FLAG_KEY,
      partnerID: partnerId,
      isBlacklist: true,
    });

    if (isNotBlacklistedPartner) {
      setUrlQueryObj(constructUrlQueryObj(latestTravelBooking));
      setShowRCupsellBanner(true);
    }
  }, [featureFlagsData]);

  return {
    showRCupsellBanner,
    isFetching,
    latestTravelBookingID: latestTravelBooking?.id,
    urlQueryObj,
    trackingData: {
      CountryOfTravelCode,
      CustomerCountryCode,
      PolicyType,
      FromDate,
      ToDate,
    },
  };
};

export default useShowRCUpsellBanner;
